import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Badge, Spinner } from 'react-bootstrap';
import { attendanceAPI } from '../services/api';
import { formatTimeIST } from '../utils/dateFormatter';
import { FaUsers, FaCalendarAlt, FaSearch } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import Swal from 'sweetalert2';

const EmployeeAttendance = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const totalWorkingHoursThreshold = Number(process.env.REACT_APP_TOTAL_WORKING_HOURS) || 8;

  useEffect(() => {
    fetchAttendanceData();
  }, [selectedDate]);

  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      const response = await attendanceAPI.getAllEmployeesAttendanceByDate(selectedDate);
      setAttendanceData(response.data);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch attendance data',
        confirmButtonColor: '#635bff',
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateWorkingHours = (punchIn, punchOut) => {
    if (!punchIn || !punchOut) return null;

    const startTime = new Date(punchIn);
    const endTime = new Date(punchOut);
    const diffMs = endTime - startTime;
    const diffHours = diffMs / (1000 * 60 * 60);

    return diffHours;
  };

  const formatWorkingHours = (hours) => {
    if (hours === null) return '-';

    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);

    if (minutes === 0) {
      return `${wholeHours}h`;
    }
    return `${wholeHours}h ${minutes}m`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      present: { bg: '#e8e5ff', color: '#635bff', text: 'PRESENT' },
      wfh: { bg: '#dcfce7', color: '#166534', text: 'WFH' },
      leave: { bg: '#fef3c7', color: '#92400e', text: 'LEAVE' },
      half_day: { bg: '#dbeafe', color: '#1e40af', text: 'HALF DAY' },
      absent: { bg: '#fee2e2', color: '#991b1b', text: 'ABSENT' }
    };

    const config = statusConfig[status] || statusConfig.absent;

    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: config.bg,
        color: config.color
      }}>
        {config.text}
      </span>
    );
  };

  const filteredData = attendanceData.filter(emp =>
    emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.employee_code && emp.employee_code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const getAttendanceSummary = () => {
    const summary = {
      present: 0,
      wfh: 0,
      leave: 0,
      half_day: 0,
      absent: 0
    };

    attendanceData.forEach(emp => {
      // Get the first attendance record for the selected date
      const attendanceRecord = emp.attendance_records && emp.attendance_records[0];
      const status = attendanceRecord?.status;
      if (status && summary.hasOwnProperty(status)) {
        summary[status]++;
      }
    });

    return summary;
  };

  const summary = getAttendanceSummary();

  return (
    <>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#0a0a0a',
          letterSpacing: '-0.02em',
          margin: 0
        }}>
          Employee Attendance
        </h1>
        <p style={{
          fontSize: '14px',
          color: '#6b7280',
          marginTop: '8px',
          marginBottom: 0
        }}>
          View date-wise attendance for all employees
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
        }}>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>Present</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#635bff' }}>{summary.present}</div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
        }}>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>WFH</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#166534' }}>{summary.wfh}</div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
        }}>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>Leave</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#92400e' }}>{summary.leave}</div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
        }}>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>Absent</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#991b1b' }}>{summary.absent}</div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
        }}>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>Total</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#0a0a0a' }}>{attendanceData.length}</div>
        </div>
      </div>

      {/* Main Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden'
      }}>
        {/* Header with Date Picker and Search */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#faf9ff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h5 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: '#0a0a0a',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <FaCalendarAlt style={{ color: '#635bff' }} />
            Attendance History
          </h5>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', minWidth: '250px' }}>
              <FaSearch style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
                fontSize: '14px'
              }} />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  paddingLeft: '36px',
                  paddingRight: '12px',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none'
                }}
              />
            </div>
            <Form.Control
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                cursor: 'pointer',
                minWidth: '160px'
              }}
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{
            padding: '60px 20px',
            textAlign: 'center'
          }}>
            <Spinner animation="border" style={{ color: '#635bff' }} />
            <div style={{ marginTop: '16px', color: '#6b7280', fontSize: '14px' }}>
              Loading attendance data...
            </div>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <Table hover responsive style={{ margin: 0 }}>
                <thead style={{ backgroundColor: '#faf9ff' }}>
                  <tr>
                    <th style={{
                      padding: '16px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb'
                    }}>#</th>
                    <th style={{
                      padding: '16px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb'
                    }}>Employee</th>
                    <th style={{
                      padding: '16px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb'
                    }}>Department</th>
                    <th style={{
                      padding: '16px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb'
                    }}>Punch In</th>
                    <th style={{
                      padding: '16px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb'
                    }}>Punch Out</th>
                    <th style={{
                      padding: '16px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb'
                    }}>Total Hours</th>
                    <th style={{
                      padding: '16px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb'
                    }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{
                        padding: '60px 20px',
                        textAlign: 'center',
                        color: '#9ca3af',
                        fontSize: '14px'
                      }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                        <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                          No employees found
                        </div>
                        <div>Try adjusting your search or date filter</div>
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((emp, index) => {
                      // Get the first attendance record for the selected date
                      const attendanceRecord = emp.attendance_records && emp.attendance_records[0];
                      return (
                        <tr key={emp.user_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            color: '#6b7280'
                          }}>
                            {indexOfFirstItem + index + 1}
                          </td>
                          <td style={{
                            padding: '16px 24px',
                            fontSize: '14px'
                          }}>
                            <div style={{ fontWeight: '500', color: '#0a0a0a', marginBottom: '2px' }}>
                              {emp.full_name}
                            </div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              {emp.email}
                            </div>
                            {emp.employee_code && (
                              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                                {emp.employee_code}
                              </div>
                            )}
                          </td>
                          <td style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            color: '#374151'
                          }}>
                            <div style={{ fontWeight: '500', marginBottom: '2px' }}>{emp.department || '-'}</div>
                            {emp.designation && (
                              <div style={{ fontSize: '13px', color: '#6b7280' }}>{emp.designation}</div>
                            )}
                          </td>
                          <td style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            color: '#374151'
                          }}>
                            {attendanceRecord?.punch_in ? formatTimeIST(attendanceRecord.punch_in) : '-'}
                          </td>
                          <td style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            color: '#374151'
                          }}>
                            {attendanceRecord?.punch_out ? formatTimeIST(attendanceRecord.punch_out) : '-'}
                          </td>
                          <td style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}>
                            {(() => {
                              const workingHours = calculateWorkingHours(
                                attendanceRecord?.punch_in,
                                attendanceRecord?.punch_out
                              );
                              const formattedHours = formatWorkingHours(workingHours);
                              const color = workingHours !== null
                                ? workingHours >= totalWorkingHoursThreshold
                                  ? '#16a34a' // green
                                  : '#dc2626' // red
                                : '#6b7280'; // gray for no data
                              return (
                                <span style={{ color }}>
                                  {formattedHours}
                                </span>
                              );
                            })()}
                          </td>
                          <td style={{
                            padding: '16px 24px'
                          }}>
                            {getStatusBadge(attendanceRecord?.status || 'absent')}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
            {filteredData.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredData.length / itemsPerPage)}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredData.length}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default EmployeeAttendance;
